import React, { useEffect, useState } from 'react';
import { Field, Input, Select, Textarea } from '../../../components/ui/FormComponents.jsx';
import { DollarSign, TrendingUp, Package } from '../../../components/icons/index.jsx';

/**
 * ComercialTab Component
 * Pestaña de formulario para información comercial del cliente
 * Incluye condiciones de venta, precios, límites de crédito y políticas comerciales
 */
export default function ComercialTab({ 
  data, 
  onChange, 
  errors = {} 
}) {
  const [vendedores, setVendedores] = useState([]);
  const [loadingVendedores, setLoadingVendedores] = useState(false);

  /**
   * Carga la lista de vendedores desde la API
   */
  useEffect(() => {
    const fetchVendedores = async () => {
      setLoadingVendedores(true);
      try {
        const tenantCode = window.location.hostname; // o desde localStorage/context
        const response = await fetch(`${import.meta.env.VITE_API_URL}/vendedores`, {
          headers: {
            'X-Tenant': tenantCode,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error('Error al cargar vendedores');
        }

        const result = await response.json();
        if (result.ok && result.data) {
          setVendedores(result.data);
        }
      } catch (error) {
        console.error('Error al cargar vendedores:', error);
        // Podrías mostrar un toast/notificación aquí
      } finally {
        setLoadingVendedores(false);
      }
    };

    fetchVendedores();
  }, []);

  /**
   * Maneja cambios en los campos del formulario
   * Propaga los cambios al componente padre
   */
  const handleChange = (field, value) => {
    onChange({ ...data, [field]: value });
  };

  /**
   * Valida los datos comerciales
   * Retorna objeto con mensajes de error para campos inválidos
   */
  const validateComercialData = () => {
    const validationErrors = {};
    
    // Validar límite de crédito (debe ser número positivo si se proporciona)
    if (data.limiteCredito && isNaN(parseFloat(data.limiteCredito))) {
      validationErrors.limiteCredito = 'El límite de crédito debe ser un número válido';
    } else if (data.limiteCredito && parseFloat(data.limiteCredito) < 0) {
      validationErrors.limiteCredito = 'El límite de crédito no puede ser negativo';
    }
    
    // Validar plazo de pago (debe ser número positivo si se proporciona)
    if (data.plazoPago && isNaN(parseInt(data.plazoPago))) {
      validationErrors.plazoPago = 'El plazo de pago debe ser un número válido';
    } else if (data.plazoPago && parseInt(data.plazoPago) < 0) {
      validationErrors.plazoPago = 'El plazo de pago no puede ser negativo';
    }

    if (data.diasVencimiento && isNaN(parseInt(data.diasVencimiento))) {
      validationErrors.diasVencimiento = 'Los días de vencimiento deben ser un número válido';
    } else if (data.diasVencimiento && parseInt(data.diasVencimiento) < 0) {
      validationErrors.diasVencimiento = 'Los días de vencimiento no pueden ser negativos';
    }

    if (data.toleranciaVencidos && isNaN(parseInt(data.toleranciaVencidos))) {
      validationErrors.toleranciaVencidos = 'La tolerancia debe ser un número válido';
    } else if (data.toleranciaVencidos && parseInt(data.toleranciaVencidos) < 0) {
      validationErrors.toleranciaVencidos = 'La tolerancia no puede ser negativa';
    }

    if (data.umbralDeuda && isNaN(parseFloat(data.umbralDeuda))) {
      validationErrors.umbralDeuda = 'El umbral de deuda debe ser un número válido';
    } else if (data.umbralDeuda && parseFloat(data.umbralDeuda) < 0) {
      validationErrors.umbralDeuda = 'El umbral de deuda no puede ser negativo';
    }
    
    return validationErrors;
  };

  /**
   * Obtiene todos los datos comerciales para envío
   * Retorna objeto completo con todos los campos comerciales
   */
  const getComercialData = () => {
    return {
      sucursal: data.sucursal || '',
      vendedor: data.vendedor || '',
      listaPrecio: data.listaPrecio || 'General',
      condicionVenta: data.condicionVenta || 'Contado',
      condicionPago: data.condicionPago || 'CONTADO',
      // Campo comercial agregado (2025-12): plazo de pago en días
      plazoPago: data.plazoPago || '',
      // Campo comercial agregado (2025-12): método de pago preferido del cliente
      metodoPago: data.metodoPago || '',
      limiteCredito: data.limiteCredito || '',
      diasVencimiento: data.diasVencimiento || '',
      toleranciaVencidos: data.toleranciaVencidos || '',
      prioridadOperativa: data.prioridadOperativa || 'NORMAL',
      bloqueoPorDeuda: data.bloqueoPorDeuda || false,
      umbralDeuda: data.umbralDeuda || '',
      slaDespachoHs: data.slaDespachoHs || '',
      slaEntregaHs: data.slaEntregaHs || '',
      // Campo comercial agregado (2025-12): zona geográfica de reparto asignada
      zonaReparto: data.zonaReparto || '',
      // Campo comercial agregado (2025-12): observaciones y notas comerciales
      observacionesComerciales: data.observacionesComerciales || '',
      activo: data.activo !== undefined ? data.activo : true,
      fechaAlta: data.fechaAlta || new Date().toISOString().slice(0, 10),
    };
  };

  return (
    <div className="space-y-5">
      {/* Clasificación del Cliente */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <TrendingUp size={18} className="text-[rgb(var(--brand))]" />
          <h3 className="text-base font-medium">Clasificación del Cliente</h3>
        </div>

        <div className="grid grid-cols-12 gap-4">
          {/* Sucursal - 6 columnas */}
          <div className="col-span-12 md:col-span-6">
            <Field label="Sucursal">
              <Input
                value={data.sucursal || ''}
                onChange={(e) => handleChange('sucursal', e.target.value)}
                placeholder="Sucursal o delegación"
              />
            </Field>
          </div>

          {/* Vendedor Asignado - 6 columnas */}
          <div className="col-span-12 md:col-span-6">
            <Field label="Vendedor Asignado">
              <Select
                value={data.vendedor || ''}
                onChange={(e) => handleChange('vendedor', e.target.value)}
                disabled={loadingVendedores}
              >
                <option value="">
                  {loadingVendedores ? 'Cargando vendedores...' : 'Seleccionar vendedor...'}
                </option>
                {vendedores.map((vendedor) => (
                  <option key={vendedor.id} value={vendedor.id}>
                    {vendedor.nombreCompleto}
                  </option>
                ))}
              </Select>
            </Field>
          </div>

          {/* Zona de Reparto - 6 columnas */}
          <div className="col-span-12 md:col-span-6">
            <Field label="Zona de Reparto">
              <Select
                value={data.zonaReparto || ''}
                onChange={(e) => handleChange('zonaReparto', e.target.value)}
              >
                <option value="">Seleccionar...</option>
                <option value="Zona Norte">Zona Norte</option>
                <option value="Zona Sur">Zona Sur</option>
                <option value="Zona Este">Zona Este</option>
                <option value="Zona Oeste">Zona Oeste</option>
                <option value="Capital Federal">Capital Federal</option>
                <option value="Gran Buenos Aires">Gran Buenos Aires</option>
                <option value="Interior">Interior</option>
                <option value="Nacional">Nacional</option>
                <option value="Sin Asignar">Sin Asignar</option>
              </Select>
            </Field>
          </div>
        </div>
      </div>

      {/* Condiciones Comerciales */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <DollarSign size={18} className="text-[rgb(var(--brand))]" />
          <h3 className="text-base font-medium">Condiciones Comerciales</h3>
        </div>

        <div className="grid grid-cols-12 gap-4">
          {/* Lista de Precios - 6 columnas */}
          <div className="col-span-12 md:col-span-6">
            <Field label="Lista de Precios">
              <Select
                value={data.listaPrecio || 'General'}
                onChange={(e) => handleChange('listaPrecio', e.target.value)}
              >
                <option value="General">General</option>
                <option value="Mayorista">Mayorista</option>
                <option value="VIP">VIP</option>
                <option value="Especial">Especial</option>
                <option value="Distribuidor">Distribuidor</option>
                <option value="Promocional">Promocional</option>
              </Select>
            </Field>
          </div>

          {/* Condición de Venta - 6 columnas */}
          <div className="col-span-12 md:col-span-6">
            <Field label="Condición de Venta">
              <Select
                value={data.condicionVenta || 'Contado'}
                onChange={(e) => handleChange('condicionVenta', e.target.value)}
              >
                <option value="Contado">Contado</option>
                <option value="30 días">30 días</option>
                <option value="60 días">60 días</option>
                <option value="90 días">90 días</option>
                <option value="Cuenta Corriente">Cuenta Corriente</option>
                <option value="Cheque">Cheque</option>
              </Select>
            </Field>
          </div>

          {/* Condición de Pago - 6 columnas */}
          <div className="col-span-12 md:col-span-6">
            <Field label="Condición de Pago">
              <Select
                value={data.condicionPago || 'CONTADO'}
                onChange={(e) => handleChange('condicionPago', e.target.value)}
              >
                <option value="CONTADO">Contado</option>
                <option value="CUENTA_CORRIENTE">Cuenta Corriente</option>
                <option value="MIXTO">Mixto</option>
              </Select>
            </Field>
          </div>

          {/* Plazo de Pago - 6 columnas */}
          <div className="col-span-12 md:col-span-6">
            <Field 
              label="Plazo de Pago (días)" 
              error={errors.plazoPago}
              hint="Cantidad de días de crédito"
            >
              <Input
                value={data.plazoPago || ''}
                onChange={(e) => handleChange('plazoPago', e.target.value)}
                placeholder="30"
                inputMode="numeric"
                type="number"
                min="0"
              />
            </Field>
          </div>

          <div className="col-span-12 md:col-span-6">
            <Field label="Días de vencimiento" error={errors.diasVencimiento}>
              <Input
                value={data.diasVencimiento || ''}
                onChange={(e) => handleChange('diasVencimiento', e.target.value)}
                placeholder="15"
                inputMode="numeric"
                type="number"
                min="0"
              />
            </Field>
          </div>

          <div className="col-span-12 md:col-span-6">
            <Field label="Tolerancia vencidos (días)" error={errors.toleranciaVencidos}>
              <Input
                value={data.toleranciaVencidos || ''}
                onChange={(e) => handleChange('toleranciaVencidos', e.target.value)}
                placeholder="3"
                inputMode="numeric"
                type="number"
                min="0"
              />
            </Field>
          </div>

          {/* Método de Pago - 6 columnas */}
          <div className="col-span-12 md:col-span-6">
            <Field label="Método de Pago Preferido">
              <Select
                value={data.metodoPago || ''}
                onChange={(e) => handleChange('metodoPago', e.target.value)}
              >
                <option value="">Seleccionar...</option>
                <option value="Transferencia">Transferencia</option>
                <option value="Efectivo">Efectivo</option>
                <option value="Cuenta Corriente">Cuenta Corriente</option>
                <option value="Cheque">Cheque</option>
                <option value="Débito Automático">Débito Automático</option>
                <option value="Tarjeta de Crédito">Tarjeta de Crédito</option>
                <option value="Tarjeta de Débito">Tarjeta de Débito</option>
              </Select>
            </Field>
          </div>

          {/* Límite de Crédito - 6 columnas */}
          <div className="col-span-12 md:col-span-6">
            <Field 
              label="Límite de Crédito" 
              error={errors.limiteCredito}
              hint="Monto máximo de crédito disponible"
            >
              <Input
                value={data.limiteCredito || ''}
                onChange={(e) => handleChange('limiteCredito', e.target.value)}
                placeholder="0.00"
                inputMode="decimal"
                type="number"
                min="0"
                step="0.01"
              />
            </Field>
          </div>

          <div className="col-span-12 md:col-span-6">
            <Field label="Prioridad operativa">
              <Select
                value={data.prioridadOperativa || 'NORMAL'}
                onChange={(e) => handleChange('prioridadOperativa', e.target.value)}
              >
                <option value="NORMAL">Normal</option>
                <option value="ALTA">Alta</option>
                <option value="VIP">VIP</option>
              </Select>
            </Field>
          </div>

          <div className="col-span-12 md:col-span-6">
            <Field label="SLA despacho (hs)">
              <Input
                value={data.slaDespachoHs || ''}
                onChange={(e) => handleChange('slaDespachoHs', e.target.value)}
                placeholder="12"
                inputMode="numeric"
                type="number"
                min="0"
              />
            </Field>
          </div>

          <div className="col-span-12 md:col-span-6">
            <Field label="SLA entrega (hs)">
              <Input
                value={data.slaEntregaHs || ''}
                onChange={(e) => handleChange('slaEntregaHs', e.target.value)}
                placeholder="24"
                inputMode="numeric"
                type="number"
                min="0"
              />
            </Field>
          </div>
        </div>
      </div>

      {/* Bloqueo por deuda */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Package size={18} className="text-[rgb(var(--brand))]" />
          <h3 className="text-base font-medium">Bloqueo por deuda</h3>
        </div>

        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-12 md:col-span-6">
            <label className="flex items-center gap-2 text-sm text-[rgb(var(--fg))]">
              <input
                type="checkbox"
                checked={data.bloqueoPorDeuda || false}
                onChange={(e) => handleChange('bloqueoPorDeuda', e.target.checked)}
                className="h-4 w-4 rounded border-[rgba(var(--border),0.6)]"
              />
              Bloqueo automático por deuda
            </label>
          </div>

          <div className="col-span-12 md:col-span-6">
            <Field label="Umbral de deuda" error={errors.umbralDeuda}>
              <Input
                value={data.umbralDeuda || ''}
                onChange={(e) => handleChange('umbralDeuda', e.target.value)}
                placeholder="150000"
                inputMode="decimal"
                type="number"
                min="0"
                step="0.01"
                disabled={!data.bloqueoPorDeuda}
              />
            </Field>
          </div>
        </div>
      </div>

      {/* Información Adicional */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Package size={18} className="text-[rgb(var(--brand))]" />
          <h3 className="text-base font-medium">Información Adicional</h3>
        </div>

        <div className="grid grid-cols-12 gap-4">
          {/* Estado - 6 columnas */}
          <div className="col-span-12 md:col-span-6">
            <Field label="Estado">
              <Select
                value={data.activo !== undefined ? (data.activo ? 'true' : 'false') : 'true'}
                onChange={(e) => handleChange('activo', e.target.value === 'true')}
              >
                <option value="true">Activo</option>
                <option value="false">Inactivo</option>
              </Select>
            </Field>
          </div>

          {/* Fecha de Alta - 6 columnas */}
          <div className="col-span-12 md:col-span-6">
            <Field label="Fecha de Alta">
              <Input
                value={data.fechaAlta || new Date().toISOString().slice(0, 10)}
                onChange={(e) => handleChange('fechaAlta', e.target.value)}
                type="date"
              />
            </Field>
          </div>

          {/* Observaciones Comerciales - 12 columnas */}
          <div className="col-span-12">
            <Field 
              label="Observaciones Comerciales"
              hint="Notas sobre descuentos especiales, condiciones particulares, acuerdos, etc."
            >
              <Textarea
                value={data.observacionesComerciales || ''}
                onChange={(e) => handleChange('observacionesComerciales', e.target.value)}
                placeholder="Ej: Cliente con descuento del 10% en compras superiores a $50.000, entrega prioritaria los lunes..."
                rows={3}
              />
            </Field>
          </div>
        </div>
      </div>
    </div>
  );
}
